/**
 * LoadServingProfile — how the load was served, in the `.rd` design language.
 * A compact restyle of the classic `LoadServingStatsCard`: a serving-mix bar
 * (on-site RE / BESS / on-site NG / grid residual), the headline coverage stats
 * from the dispatch summary, and a daily load sparkline when the per-day series
 * is present.
 *
 * This is a physical dispatch view (energy served), not an impact number, so it
 * is lens-invariant — it does not change with Consequential ⇄ Attributional.
 */

import { useMemo } from "react";
import type { DispatchSummary, DailyDispatchPoint } from "../../load-portfolio/domain/types";

export interface LoadServingProfileProps {
  dispatch: DispatchSummary;
  daily?: DailyDispatchPoint[];
}

const MIX = [
  { key: "re", label: "On-site RE", color: "#5aa9a3" },
  { key: "bess", label: "BESS", color: "#8a7bbf" },
  { key: "ng", label: "On-site NG", color: "#b0642f" },
  { key: "grid", label: "Grid residual", color: "#3f7fb0" },
] as const;

const fmtMwh = (n: number) => {
  const a = Math.abs(n);
  if (a >= 1e6) return `${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${(a / 1e3).toFixed(0)}K`;
  return `${a.toFixed(0)}`;
};
const pct1 = (n: number) => `${n.toFixed(1)}%`;

export function LoadServingProfile({ dispatch, daily }: LoadServingProfileProps) {
  const load = dispatch.total_load_mwh || 0;
  const re = dispatch.total_re_served_mwh || 0;
  const bess = dispatch.total_bess_discharged_mwh || 0;
  const ng = dispatch.total_ng_gen_mwh || 0;
  // The engine's residual (unserved-by-onsite) is met by the grid's marginal
  // mix — the consequential model treats it as grid-supplied.
  const grid = Math.max(0, load - re - bess - ng);
  const denom = re + bess + ng + grid || 1;
  const mixPct: Record<string, number> = {
    re: (re / denom) * 100,
    bess: (bess / denom) * 100,
    ng: (ng / denom) * 100,
    grid: (grid / denom) * 100,
  };

  const stats = [
    { n: `${fmtMwh(load)}`, u: " MWh", k: "total load" },
    { n: pct1(dispatch.re_coverage_pct ?? 0), k: "RE coverage" },
    { n: pct1(mixPct.grid), k: "grid share" },
    { n: pct1((dispatch.ng_capacity_factor ?? 0) * 100), k: "NG capacity factor" },
    { n: `${(dispatch.bess_cycles ?? 0).toFixed(0)}`, k: "BESS cycles" },
    { n: `${fmtMwh(dispatch.total_curtailed_mwh ?? 0)}`, u: " MWh", k: "curtailed" },
  ];

  const spark = useMemo(() => {
    const pts = daily ?? [];
    if (pts.length === 0) return null;
    const W = 300;
    const H = 70;
    const PAD = 4;
    const max = Math.max(...pts.map((p) => p.load), 1e-9);
    const n = pts.length;
    const line = pts
      .map((p, i) => {
        const x = n === 1 ? W / 2 : (i / (n - 1)) * W;
        const y = H - PAD - (p.load / max) * (H - PAD * 2);
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");
    return { line, area: `${line} L${W},${H} L0,${H} Z` };
  }, [daily]);

  const unserved = dispatch.total_unserved_mwh ?? 0;

  return (
    <div className="card" style={{ margin: 0 }}>
      <div className="ch">
        <h2>How the load was served</h2>
        <span className="mut">dispatch · lens-invariant</span>
      </div>
      <div className="cb">
        <div className="fuelbar">
          {MIX.map((m) =>
            mixPct[m.key] > 0 ? (
              <span
                key={m.key}
                style={{ width: `${mixPct[m.key]}%`, background: m.color }}
                title={`${m.label} — ${mixPct[m.key].toFixed(0)}%`}
              />
            ) : null,
          )}
        </div>
        <div className="flegend" style={{ marginBottom: 14 }}>
          {MIX.map((m) =>
            mixPct[m.key] > 0 ? (
              <span key={m.key}>
                <i style={{ background: m.color }} />
                {m.label} {mixPct[m.key].toFixed(0)}%
              </span>
            ) : null,
          )}
        </div>

        <div className="statrow">
          {stats.map((s) => (
            <div className="s" key={s.k}>
              <div className="n">
                {s.n}
                {s.u && <span style={{ fontSize: 12, opacity: 0.7 }}>{s.u}</span>}
              </div>
              <div className="k">{s.k}</div>
            </div>
          ))}
        </div>

        {spark && (
          <>
            <div className="genk">Daily load</div>
            <svg className="spark" viewBox="0 0 300 70" preserveAspectRatio="none">
              <defs>
                <linearGradient id="lrd-load" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="var(--accent)" stopOpacity=".3" />
                  <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={spark.area} fill="url(#lrd-load)" />
              <path d={spark.line} fill="none" stroke="var(--accent)" strokeWidth={1.6} />
            </svg>
          </>
        )}

        {unserved > 1 && (
          <div className="lwarn" style={{ marginTop: 14 }}>
            {fmtMwh(unserved)} MWh of load went unserved
            {dispatch.hours_unserved != null && (
              <> across {dispatch.hours_unserved.toLocaleString()} hours</>
            )}
            {dispatch.max_unserved_mw != null && (
              <> (peak shortfall {dispatch.max_unserved_mw.toFixed(0)} MW)</>
            )}
            . This is a partially-islanded configuration.
          </div>
        )}
      </div>
    </div>
  );
}
